/**
 * Wishlist機能
 * LocalStorage使用、ログイン必須
 * キー名: wishlistItem_{customerId}
 */

class Wishlist {
  constructor() {
    // ログイン状態とカスタマーIDを取得
    this.isLoggedIn = window.isCustomerLoggedIn || false;
    this.customerId = window.customerId || null;
    
    // ユーザーごとにLocalStorageキーを分ける
    this.storageKey = this.isLoggedIn && this.customerId 
      ? `wishlistItem_${this.customerId}` 
      : 'wishlistItem_guest';
    
    this.init();
  }

  init() {
    // カウント更新（未ログイン時は空文字列をセットするだけ）
    this.updateCount();
    
    // ログイン時のみボタンのUI更新
    if (this.isLoggedIn) {
      this.updateButtonUI();
    }
    
    // イベントバインドは常に必要（ログインモーダル表示のため）
    this.bindEvents();
  }

  // LocalStorageから取得（ログイン時のみ）
  getWishlist() {
    if (!this.isLoggedIn) {
      return [];
    }
    
    const items = localStorage.getItem(this.storageKey);
    return items ? JSON.parse(items) : [];
  }

  // LocalStorageに保存（ログイン時のみ）
  saveWishlist(items) {
    if (!this.isLoggedIn) {
      return;
    }
    
    localStorage.setItem(this.storageKey, JSON.stringify(items));
    this.updateButtonUI();
    this.updateCount();
  }

  // 商品を追加（ログイン時のみ）
  addItem(handle) {
    if (!this.isLoggedIn) {
      return false;
    }
    
    const wishlist = this.getWishlist();
    if (!wishlist.includes(handle)) {
      wishlist.push(handle);
      this.saveWishlist(wishlist);
      return true;
    }
    return false;
  }

  // 商品を削除（ログイン時のみ）
  removeItem(handle) {
    if (!this.isLoggedIn) {
      return;
    }
    
    let wishlist = this.getWishlist();
    wishlist = wishlist.filter(item => item !== handle);
    this.saveWishlist(wishlist);
  }

  // 商品が含まれているか確認（ログイン時のみ）
  hasItem(handle) {
    if (!this.isLoggedIn) {
      return false;
    }
    
    return this.getWishlist().includes(handle);
  }

  // wishlistボタンのUI更新（ログイン時のみ使用）
  updateButtonUI() {
    const buttons = document.querySelectorAll('[data-wishlist-handle]');
    const wishlist = this.getWishlist();
    
    buttons.forEach(button => {
      const handle = button.dataset.wishlistHandle;
      if (wishlist.includes(handle)) {
        button.classList.add('added');
      } else {
        button.classList.remove('added');
      }
    });
  }

  // wishlistカウント更新
  updateCount() {
    const counters = document.querySelectorAll('[data-wishlist-count]');
    
    // 未ログイン時は常に空文字列（非表示）
    if (!this.isLoggedIn) {
      counters.forEach(counter => {
        counter.textContent = '';
      });
      return;
    }
    
    // ログイン時のみカウントを表示
    const count = this.getWishlist().length;
    counters.forEach(counter => {
      // カウントが0の時は空文字列（非表示）、それ以外は数字を表示
      counter.textContent = count > 0 ? count : '';
    });
  }

  // イベントバインド
  bindEvents() {
    // wishlistボタンのクリック
    document.addEventListener('click', (e) => {
      const button = e.target.closest('[data-wishlist-handle]');
      if (!button) return;

      e.preventDefault();

      // ログインチェック
      if (!this.isLoggedIn) {
        this.showLoginModal();
        return;
      }

      const handle = button.dataset.wishlistHandle;

      if (this.hasItem(handle)) {
        this.removeItem(handle);
      } else {
        this.addItem(handle);
      }
    });

    // ヘッダーのお気に入りアイコン（未ログイン時）
    document.addEventListener('click', (e) => {
      const trigger = e.target.closest('[data-wishlist-login-trigger]');
      if (!trigger) return;

      e.preventDefault();
      this.showLoginModal();
    });
  }

  // ログインモーダル表示（modal.js統一システムを使用）
  showLoginModal() {
    if (window.modalSystem) {
      window.modalSystem.open('wishlist-login-modal');
    }
  }

  // wishlistページ: 商品データを取得してHTML生成（ログイン時のみ）
  async loadWishlistPage() {
    const container = document.querySelector('[data-wishlist-container]');
    
    if (!container) return;

    // 未ログイン時は空のメッセージを表示
    if (!this.isLoggedIn) {
      this.showEmptyMessage(container);
      return;
    }

    const wishlist = this.getWishlist();

    if (wishlist.length === 0) {
      this.showEmptyMessage(container);
      return;
    }

    container.innerHTML = '<div class="loading">読み込み中...</div>';

    try {
      const products = await this.fetchProducts(wishlist);
      this.renderProducts(container, products);
    } catch (error) {
      console.error('Wishlist load error:', error);
      container.innerHTML = '<p class="error">商品の読み込みに失敗しました</p>';
    }
  }

  // 商品データを取得
  async fetchProducts(handles) {
    const products = [];
    
    for (const handle of handles) {
      try {
        const response = await fetch(`/products/${handle}.js`);
        if (response.ok) {
          const product = await response.json();
          products.push(product);
        }
      } catch (error) {
        console.error(`Failed to fetch product: ${handle}`, error);
      }
    }
    
    return products;
  }

  // 商品をレンダリング
  renderProducts(container, products) {
    const grid = document.createElement('div');
    grid.className = 'wishlist-grid';

    products.forEach(product => {
      const item = this.createProductCard(product);
      grid.appendChild(item);
    });

    container.innerHTML = '';
    container.appendChild(grid);
  }

  // 商品カードを作成
  createProductCard(product) {
    const div = document.createElement('div');
    div.className = 'wishlist-item';
    div.dataset.productHandle = product.handle;

    const image = product.images[0] || '';
    const formattedPrice = window.formatPrice(product.price);
    const formattedComparePrice = product.compare_at_price ? window.formatPrice(product.compare_at_price) : null;

    div.innerHTML = `
      <div class="wishlist-item__image">
        <a href="/products/${product.handle}">
          <img src="${image}" alt="${product.title}" loading="lazy">
        </a>
      </div>
      <div class="wishlist-item__info">
        <h3 class="wishlist-item__title">
          <a href="/products/${product.handle}">${product.title}</a>
        </h3>
        <div class="wishlist-item__price">
          ${formattedComparePrice ? `<s class="price--compare">${formattedComparePrice}</s>` : ''}
          <span class="price--sale">${formattedPrice}</span>
        </div>
        <div class="wishlist-item__actions">
          <button type="button" 
                  class="button button--primary" 
                  data-add-to-cart="${product.variants[0].id}">
            カートに追加
          </button>
          <button type="button" 
                  class="button button--secondary" 
                  data-wishlist-remove="${product.handle}">
            削除
          </button>
        </div>
      </div>
    `;

    return div;
  }

  // 空のメッセージ表示
  showEmptyMessage(container) {
    container.innerHTML = `
      <div class="wishlist-empty">
        <p class="wishlist-empty__text">お気に入りに商品がありません</p>
        <a href="/collections/all" class="button button--primary">
          ショッピングを続ける
        </a>
      </div>
    `;
  }
}

// 初期化
document.addEventListener('DOMContentLoaded', () => {
  window.wishlist = new Wishlist();

  // wishlistページの場合、商品を読み込む
  if (document.querySelector('[data-wishlist-container]')) {
    window.wishlist.loadWishlistPage();
  }
});

// wishlistから削除
document.addEventListener('click', (e) => {
  const removeBtn = e.target.closest('[data-wishlist-remove]');
  if (removeBtn) {
    const handle = removeBtn.dataset.wishlistRemove;
    window.wishlist.removeItem(handle);
    
    // DOM要素を削除
    const item = removeBtn.closest('.wishlist-item');
    if (item) {
      item.remove();
    }

    // 空になったらメッセージ表示
    if (window.wishlist.getWishlist().length === 0) {
      const container = document.querySelector('[data-wishlist-container]');
      window.wishlist.showEmptyMessage(container);
    }
  }
});

// カートに追加
document.addEventListener('click', async (e) => {
  const addBtn = e.target.closest('[data-add-to-cart]');
  if (!addBtn) return;

  const variantId = addBtn.dataset.addToCart;
  addBtn.disabled = true;
  addBtn.textContent = '追加中...';

  try {
    const response = await fetch('/cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        id: variantId,
        quantity: 1
      })
    });

    if (response.ok) {
      addBtn.textContent = '追加しました';
      
      // カート数を更新
      const cartResponse = await fetch('/cart.js');
      const cart = await cartResponse.json();
      document.querySelectorAll('[data-cart-count]').forEach(counter => {
        counter.textContent = cart.item_count;
      });

      setTimeout(() => {
        addBtn.disabled = false;
        addBtn.textContent = 'カートに追加';
      }, 2000);
    }
  } catch (error) {
    console.error('Add to cart error:', error);
    addBtn.disabled = false;
    addBtn.textContent = 'エラー';
  }
});
